import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Phone, 
  Clock, 
  CheckCircle2, 
  FileSpreadsheet,
  Zap,
  Flame,
  AlertTriangle,
  Home,
  Award,
  Building2,
  Ruler,
  Droplets,
  FileCheck,
  Star,
  Calendar,
  Euro,
  Wine,
  Landmark
} from 'lucide-react';

const DiagnosticOrange = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const villesVaucluse = [
    "Orange 84100", "Courthézon 84350", "Châteauneuf-du-Pape 84230",
    "Bédarrides 84370", "Sorgues 84700", "Vedène 84270",
    "Le Pontet 84130", "Entraigues-sur-la-Sorgue 84320", "Monteux 84170",
    "Carpentras 84200", "Sarrians 84260", "Aubignan 84810",
    "Beaumes-de-Venise 84190", "Vacqueyras 84190", "Gigondas 84190",
    "Sablet 84110", "Séguret 84110", "Roaix 84110"
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-32 pb-16 px-4 bg-gradient-to-br from-white to-gray-100"
    >
      <div className="container mx-auto max-w-4xl">
        {/* Hero Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.div variants={itemVariants} className="flex justify-center mb-6">
            <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue p-4 rounded-full">
              <MapPin className="h-12 w-12 text-white" />
            </div>
          </motion.div>
          <motion.h1 variants={itemVariants} className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Diagnostic Immobilier Orange et Vaucluse (84)
          </motion.h1>
          <motion.p variants={itemVariants} className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Expert certifié en diagnostics immobiliers à Orange, Châteauneuf-du-Pape, Courthézon et dans tout le Vaucluse. 
            DPE, amiante, électricité, gaz, termites - Intervention rapide dans le 84.
          </motion.p>
        </motion.div>

        {/* Article Content */}
        <motion.article
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="prose prose-lg max-w-none"
        >
          {/* Introduction */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center">
              <Award className="h-8 w-8 text-dk-blue mr-4" />
              Diagnostics Immobiliers dans le Vaucluse : Votre Expert de Confiance
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              Le <strong>Vaucluse (84)</strong> est un département riche en patrimoine où le marché immobilier 
              nécessite une expertise pointue. D'<strong>Orange</strong>, ville d\'art et d'histoire avec son 
              théâtre antique, à <strong>Châteauneuf-du-Pape</strong> et ses prestigieux vignobles, en passant 
              par <strong>Courthézon</strong>, <strong>Carpentras</strong> ou <strong>Sorgues</strong>, chaque 
              transaction immobilière doit respecter les obligations légales en matière de diagnostic.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              <strong>DIAGNOSTI-K</strong>, votre diagnostiqueur immobilier certifié, intervient dans tout le 
              Vaucluse pour accompagner propriétaires, acquéreurs, bailleurs et professionnels de l'immobilier. 
              Basé à Camaret-sur-Aigues, nous nous déplaçons rapidement sur <strong>Orange 84100</strong>, 
              <strong>Châteauneuf-du-Pape 84230</strong>, <strong>Courthézon 84350</strong> et dans l'ensemble 
              des communes vauclusiennes.
            </p>

            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircle2 className="h-6 w-6 text-dk-blue mr-3" />
                Pourquoi choisir DIAGNOSTI-K dans le Vaucluse ?
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Star className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Expertise locale</strong> : Parfaite connaissance du territoire vauclusien</span>
                </li>
                <li className="flex items-start">
                  <Clock className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Proximité</strong> : Basé à Camaret-sur-Aigues, intervention rapide</span>
                </li>
                <li className="flex items-start">
                  <FileCheck className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Qualité</strong> : Rapports détaillés remis sous 72h</span>
                </li>
                <li className="flex items-start">
                  <Euro className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Transparence</strong> : Devis gratuit et tarifs compétitifs</span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Services détaillés */}
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-8 flex items-center">
              <FileSpreadsheet className="h-8 w-8 mr-4" />
              Nos Services de Diagnostic dans le Vaucluse
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <FileSpreadsheet className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">DPE Orange et Vaucluse</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Le <strong>Diagnostic de Performance Énergétique</strong> est obligatoire pour toute vente ou location 
                  dans le Vaucluse. Nous réalisons vos DPE à Orange, Châteauneuf-du-Pape, Carpentras et dans 
                  toutes les communes du 84.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Évaluation énergétique complète</li>
                  <li>• Classement de A à G</li>
                  <li>• Recommandations d'amélioration</li>
                  <li>• Validité 10 ans</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Zap className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">Diagnostic Électricité Vaucluse</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Vérification complète des installations électriques dans les logements de plus de 15 ans 
                  à Courthézon, Bédarrides, Sorgues, Vedène et dans tout le Vaucluse.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Contrôle de conformité</li>
                  <li>• Détection des anomalies</li>
                  <li>• Sécurité des occupants</li>
                  <li>• Validité 3 ans (vente) / 6 ans (location)</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Flame className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">Diagnostic Gaz Vaucluse</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Contrôle sécuritaire des installations gaz dans les logements vauclusiens équipés d'installations 
                  de plus de 15 ans. Intervention au Pontet, Entraigues-sur-la-Sorgue, Monteux et environs.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Vérification des canalisations</li>
                  <li>• Test d'étanchéité</li>
                  <li>• Contrôle des appareils</li>
                  <li>• Prévention des risques</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">Diagnostic Amiante Vaucluse</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Recherche d'amiante obligatoire dans les bâtiments construits avant 1997 dans le Vaucluse. 
                  Expertise à Sarrians, Aubignan, Beaumes-de-Venise et alentours.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Repérage des matériaux amiantés</li>
                  <li>• Évaluation de l'état de conservation</li>
                  <li>• Recommandations de gestion</li>
                  <li>• Protection de la santé</li>
                </ul>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">Autres diagnostics disponibles dans le Vaucluse :</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center">
                  <Home className="h-5 w-5 mr-2" />
                  <span>Termites</span>
                </div>
                <div className="flex items-center">
                  <Droplets className="h-5 w-5 mr-2" />
                  <span>Plomb</span>
                </div>
                <div className="flex items-center">
                  <Ruler className="h-5 w-5 mr-2" />
                  <span>Carrez</span>
                </div>
                <div className="flex items-center">
                  <FileCheck className="h-5 w-5 mr-2" />
                  <span>ERP</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Zone d'intervention */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center">
              <MapPin className="h-8 w-8 text-dk-blue mr-4" />
              Zone d'Intervention dans le Vaucluse
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              Notre diagnostiqueur certifié intervient dans l'ensemble du département du <strong>Vaucluse (84)</strong> 
              et ses principales communes. Que vous soyez à Orange, dans les villages viticoles des Côtes du Rhône, 
              ou dans les communes rurales, nous nous déplaçons rapidement pour réaliser vos diagnostics immobiliers.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Building2 className="h-6 w-6 text-dk-blue mr-3" />
                  Région d'Orange
                </h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Orange 84100</strong> - Ville d'art et d'histoire</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Courthézon 84350</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Châteauneuf-du-Pape 84230</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Bédarrides 84370</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Sorgues 84700</strong></span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Home className="h-6 w-6 text-dk-blue mr-3" />
                  Région de Carpentras
                </h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Carpentras 84200</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Monteux 84170</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Sarrians 84260</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Aubignan 84810</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Beaumes-de-Venise 84190</strong></span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Toutes nos communes d'intervention dans le Vaucluse :</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                {villesVaucluse.map((ville, index) => (
                  <div key={index} className="flex items-center">
                    <MapPin className="h-4 w-4 text-dk-blue mr-2 flex-shrink-0" />
                    <span className="font-medium">{ville}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Spécificités locales */}
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Award className="h-8 w-8 mr-4" />
              Spécificités du Marché Immobilier Vauclusien
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white/10 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Wine className="h-6 w-6 mr-3" />
                  Patrimoine Viticole
                </h3>
                <p className="text-white/90 mb-4">
                  Le Vaucluse, notamment <strong>Châteauneuf-du-Pape</strong>, <strong>Gigondas</strong> et 
                  <strong>Vacqueyras</strong>, possède un patrimoine viticole exceptionnel avec des propriétés 
                  anciennes nécessitant des diagnostics spécialisés.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Diagnostic amiante pour caves anciennes</li>
                  <li>• DPE adapté aux bâtiments viticoles</li>
                  <li>• Expertise des chais et dépendances</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Landmark className="h-6 w-6 mr-3" />
                  Patrimoine Historique
                </h3>
                <p className="text-white/90 mb-4">
                  <strong>Orange</strong> avec son théâtre antique et <strong>Carpentras</strong> avec son centre 
                  historique comptent de nombreux bâtiments anciens nécessitant une expertise particulière.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Diagnostic plomb (bâtiments avant 1949)</li>
                  <li>• Amiante dans constructions anciennes</li>
                  <li>• Respect du patrimoine architectural</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Processus et délais */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center">
              <Clock className="h-8 w-8 text-dk-blue mr-4" />
              Notre Processus dans le Vaucluse
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Contact</h3>
                <p className="text-gray-600 text-sm">
                  Appelez-nous ou demandez un devis en ligne. Réponse rapide pour tout le Vaucluse.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Intervention</h3>
                <p className="text-gray-600 text-sm">
                  Déplacement sous 48h à Orange, Châteauneuf-du-Pape ou dans votre commune.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Rapport</h3>
                <p className="text-gray-600 text-sm">
                  Remise des diagnostics sous 72h maximum par email et courrier.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="h-6 w-6 text-dk-blue mr-3" />
                Horaires d'Intervention dans le Vaucluse
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="font-medium text-gray-900">Lundi - Vendredi</p>
                  <p className="text-gray-700">8h00 - 19h00</p>
                  <p className="text-sm text-gray-600">Interventions sur rendez-vous</p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Samedi</p>
                  <p className="text-gray-700">Sur rendez-vous</p>
                  <p className="text-sm text-gray-600">Selon disponibilités</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Besoin d'un Diagnostic dans le Vaucluse ?</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Contactez DIAGNOSTI-K pour tous vos diagnostics immobiliers à Orange, Châteauneuf-du-Pape, 
              Carpentras et dans tout le Vaucluse. Intervention rapide et tarifs transparents.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a 
                href="tel:0789307500"
                className="btn bg-white text-dk-blue hover:bg-gray-100 text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="h-5 w-5 mr-2" />
                07 89 30 75 00
              </a>
              <Link
                to="/contact"
                className="btn bg-white/10 hover:bg-white/20 text-white border-2 border-white text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                Demander un devis
              </Link>
            </div>
          </motion.div>
        </motion.article>
      </div>
    </motion.div>
  );
};

export default DiagnosticOrange;